import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ApiRequestService } from '../../shared/api-request.service';
import { RBACService } from '../../shared/rbac.service';

/**
 * Holds the account's pending membership request count (SC-301).
 *
 * The count appears in two places, the left navigation entry and the Access Requests tab label, so it is
 * held once here rather than fetched independently by each. A queue nobody notices leaves people unable to
 * work, and two counts that disagree are worse than one that is occasionally stale.
 *
 * Exposed as a BehaviorSubject so the navigation picks up an approve or decline that happened on the list
 * without either component knowing about the other.
 */
@Injectable({
  providedIn: 'root'
})
export class AccessRequestsService {
  private pendingCountSubject = new BehaviorSubject<number>(0);

  public pendingCount: Observable<number> = this.pendingCountSubject.asObservable();

  constructor(
    private api: ApiRequestService,
    private rbac: RBACService
  ) {}

  /**
   * Refreshes the pending count.
   *
   * Silently does nothing without the view permission, so it is safe to call from shared entry points
   * such as sign in and account switch without every caller repeating the permission test. A user without
   * the permission simply keeps a zero count, and zero renders no badge.
   */
  refreshPendingCount(): void {
    if ( !this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_EMPLOYEES_ACCESS_REQUESTS, RBACService.RBAC_A.A_VIEW)
    ], true) ) {
      this.pendingCountSubject.next(0);
      return;
    }

    // The endpoint answers through sendApiResponse(), so the count sits under data rather than at the
    // top level. Reading response.pending here would silently always be undefined and the badge would
    // never appear.
    this.api.makeRequest('get', 'v2/account-access-requests/meta')
      .then((response: { data: { pending: number } }): void => {
        this.pendingCountSubject.next(response?.data?.pending || 0);
      })
      .catch((): void => {
        // A failed badge fetch must never surface an error to the user: the badge is an aid, not a
        // function. Fall back to no badge.
        this.pendingCountSubject.next(0);
      });
  }

  /**
   * Clears the count, for sign out and account switch, so a stale badge from the previous account cannot
   * bleed into the next one.
   */
  clearPendingCount(): void {
    this.pendingCountSubject.next(0);
  }
}
